import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Play, Pause, Share2, Monitor, QrCode, ArrowLeft } from 'lucide-react';
import { getSession, updateSession } from '../services/api';
import websocketService from '../services/websocket';
import QRCodeModal from '../components/QRCodeModal';
import PDFSlideViewer from '../components/PDFSlideViewer';
import AIDepthGenerator from '../components/AIDepthGenerator';

const PresenterDashboard = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLive, setIsLive] = useState(false);
  const [editingDepth, setEditingDepth] = useState('');
  const [loading, setLoading] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    // Load session and presentation
    const loadSession = async () => {
      try {
        const data = await getSession(sessionId);
        setSession(data.session);
        setPresentation(data.presentation);
        setCurrentSlideIndex(data.session.current_slide);
        setIsLive(data.session.is_live);
        if (data.presentation.slides[data.session.current_slide]) {
          setEditingDepth(data.presentation.slides[data.session.current_slide].depth_notes || '');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading session:', error);
        setLoading(false);
      }
    };

    loadSession();

    // Connect to WebSocket
    const setupWebSocket = async () => {
      try {
        await websocketService.connect();
        await websocketService.joinSession(sessionId);
        console.log('PresenterDashboard: WebSocket setup complete');
      } catch (error) {
        console.error('PresenterDashboard: WebSocket setup failed:', error);
      }
    };

    setupWebSocket();

    return () => {
      websocketService.leaveSession(sessionId);
    };
  }, [sessionId]);

  useEffect(() => {
    if (presentation && presentation.slides[currentSlideIndex]) {
      setEditingDepth(presentation.slides[currentSlideIndex].depth_notes || '');
    }
  }, [currentSlideIndex, presentation]);

  const goToPrevious = async () => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      setCurrentSlideIndex(newIndex);
      try {
        await updateSession(sessionId, { current_slide: newIndex });
      } catch (error) {
        console.error('Error updating slide:', error);
      }
    }
  };

  const goToNext = async () => {
    if (presentation && currentSlideIndex < presentation.slides.length - 1) {
      const newIndex = currentSlideIndex + 1;
      setCurrentSlideIndex(newIndex);
      try {
        await updateSession(sessionId, { current_slide: newIndex });
      } catch (error) {
        console.error('Error updating slide:', error);
      }
    }
  };

  const goToSlide = async (index) => {
    if (presentation && index >= 0 && index < presentation.slides.length) {
      setCurrentSlideIndex(index);
      if (presentation.slides[index]) {
        setEditingDepth(presentation.slides[index].depth_notes || '');
      }
      try {
        await updateSession(sessionId, { current_slide: index });
      } catch (error) {
        console.error('Error updating slide:', error);
      }
    }
  };

  const toggleLive = async () => {
    const newLiveState = !isLive;
    setIsLive(newLiveState);
    try {
      await updateSession(sessionId, { is_live: newLiveState });
    } catch (error) {
      console.error('Error toggling live status:', error);
    }
  };

  const updateDepthNotes = () => {
    if (presentation && presentation.slides[currentSlideIndex]) {
      const slideId = presentation.slides[currentSlideIndex].id;
      websocketService.emitDepthNotesUpdate(sessionId, slideId, editingDepth);
      
      // Update local state
      const updatedSlides = [...presentation.slides];
      updatedSlides[currentSlideIndex].depth_notes = editingDepth;
      setPresentation({ ...presentation, slides: updatedSlides });
    }
  };

  const copyViewerLink = () => {
    const link = `${window.location.origin}/view/${sessionId}`;
    navigator.clipboard.writeText(link);
    alert('Viewer link copied to clipboard!');
  };

  const goBack = () => {
    // Navigate back to upload page (we can't go back to step 3 without state)
    // So we navigate to the home page
    navigate('/');
  };

  const viewerLink = `${window.location.origin}/view/${sessionId}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Loading session...</p>
      </div>
    );
  }

  if (!session || !presentation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Session not found</p>
      </div>
    );
  }

  const currentSlide = presentation.slides[currentSlideIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Back Button */}
        <Button
          onClick={goBack}
          variant="ghost"
          className="mb-4 gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Button>
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                {presentation.title}
              </h1>
              <p className="text-slate-600">Presenter Dashboard</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={isLive ? "default" : "secondary"} className="text-sm px-4 py-2">
                {isLive ? (
                  <><Monitor className="w-3 h-3 mr-1 inline" />Live</>
                ) : (
                  <>Offline</>
                )}
              </Badge>
              <Button onClick={toggleLive} className={isLive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}>
                {isLive ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {isLive ? 'Stop' : 'Go Live'}
              </Button>
              <Button onClick={() => setShowQRModal(true)} variant="outline" className="gap-2">
                <QrCode className="w-4 h-4" />
                QR Code
              </Button>
              <Button onClick={copyViewerLink} variant="outline" className="gap-2">
                <Share2 className="w-4 h-4" />
                Share Link
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Slide Thumbnails */}
          <div className="w-64 flex-shrink-0">
            <Card className="border-slate-200 shadow-sm sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-700">Slides</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  {presentation.slides.map((slide, index) => (
                    <button
                      key={slide.id}
                      onClick={() => goToSlide(index)}
                      className={`w-full text-left rounded-lg border-2 transition-all hover:border-blue-300 ${
                        index === currentSlideIndex 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {/* Slide Number Badge */}
                      <div className="absolute top-1 left-1 bg-slate-900 text-white text-xs px-2 py-0.5 rounded z-10">
                        {index + 1}
                      </div>
                      
                      {/* Slide Preview */}
                      <div 
                        className="relative aspect-video overflow-hidden rounded-t-md"
                        style={{
                          background: slide?.background?.type === 'color' 
                            ? slide.background.value 
                            : slide?.background?.type === 'gradient'
                            ? slide.background.value
                            : slide?.background?.type === 'image'
                            ? `url(${slide.background.value}) center/cover`
                            : '#ffffff'
                        }}
                      >
                        {presentation.pdf_data && slide?.pdf_page_number > 0 ? (
                          <PDFSlideViewer 
                            key={`thumb-${slide.id}-page-${slide.pdf_page_number}`}
                            pdfData={presentation.pdf_data} 
                            pageNumber={slide.pdf_page_number}
                            width={240}
                          />
                        ) : (
                          <div className="p-2 h-full flex flex-col justify-center">
                            <p 
                              className="text-xs font-semibold text-slate-800 line-clamp-2"
                              dangerouslySetInnerHTML={{ __html: slide.title || 'Untitled' }}
                            />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
            {/* Current Slide */}
            <Card className="border-slate-200 shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-slate-900">Current Slide ({currentSlideIndex + 1}/{presentation.slides.length})</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={goToPrevious}
                      disabled={currentSlideIndex === 0}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={goToNext}
                      disabled={currentSlideIndex === presentation.slides.length - 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div 
                  className="rounded-lg p-4 sm:p-8 border-2 border-blue-200" 
                  style={{ 
                    minHeight: '500px', 
                    maxHeight: '75vh', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: currentSlide?.background?.type === 'color' 
                      ? currentSlide.background.value 
                      : currentSlide?.background?.type === 'gradient'
                      ? currentSlide.background.value
                      : currentSlide?.background?.type === 'image'
                      ? `url(${currentSlide.background.value}) center/cover`
                      : '#ffffff'
                  }}
                >
                  {presentation.pdf_data && currentSlide?.pdf_page_number > 0 ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <PDFSlideViewer 
                        key={`current-slide-${currentSlideIndex}-page-${currentSlide.pdf_page_number}`}
                        pdfData={presentation.pdf_data} 
                        pageNumber={currentSlide.pdf_page_number}
                        maxWidth={800}
                        maintainAspectRatio={false}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col justify-center">
                      <h2 
                        className="text-2xl font-bold text-slate-900 mb-4"
                        dangerouslySetInnerHTML={{ __html: currentSlide.title || '' }}
                      />
                      <div 
                        className="text-slate-700 text-lg"
                        dangerouslySetInnerHTML={{ __html: currentSlide.content || '' }}
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Depth Layer Editor */}
          <div className="w-96 flex-shrink-0">
            <Card className="border-blue-200 shadow-md sticky top-6">
              <CardHeader>
                <CardTitle className="text-slate-900">Depth Layer</CardTitle>
                <p className="text-sm text-slate-600">Additional content for viewers</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-700">Depth Notes</Label>
                  <Textarea
                    value={editingDepth}
                    onChange={(e) => setEditingDepth(e.target.value)}
                    placeholder="Add detailed explanations, links, or additional context"
                    rows={12}
                    className="mt-2 border-blue-200"
                  />
                  <AIDepthGenerator
                    slideTitle={
                      presentation && presentation.slides[currentSlideIndex]
                        ? presentation.slides[currentSlideIndex].title
                        : ''
                    }
                    slideContent={
                      presentation && presentation.slides[currentSlideIndex]
                        ? presentation.slides[currentSlideIndex].content
                        : ''
                    }
                    onGenerated={(formattedNotes) => {
                      setEditingDepth(formattedNotes);
                    }}
                    disabled={!presentation || !presentation.slides[currentSlideIndex]}
                  />
                </div>
                <Button
                  onClick={updateDepthNotes}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Update Depth Notes
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={viewerLink}
        title="Share Viewer Link"
      />
    </div>
  );
};

export default PresenterDashboard;
